import React, { useMemo, useState } from 'react';
import {
  UserPlus,
  Upload,
  Download,
  FileText,
  CheckCircle,
  AlertCircle,
  Loader2,
} from 'lucide-react';
import {
  LEGACY_CUSTOMER_FIELDS,
  LegacyCustomerImportService,
  downloadLegacyCustomerSampleCsv,
  parseLegacyCustomerCsv,
} from '../../services/legacyCustomerImportService';

const EMPTY_FORM = LEGACY_CUSTOMER_FIELDS.reduce((acc, field) => {
  acc[field.key] = '';
  return acc;
}, {});

const AddCustomer = () => {
  const [activeTab, setActiveTab] = useState('single');
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const [selectedFile, setSelectedFile] = useState(null);
  const [previewRows, setPreviewRows] = useState([]);
  const [csvUploading, setCsvUploading] = useState(false);
  const [csvProgress, setCsvProgress] = useState({ total: 0, processed: 0, imported: 0, failed: 0 });
  const [csvErrors, setCsvErrors] = useState([]);

  const requiredFields = useMemo(
    () => LEGACY_CUSTOMER_FIELDS.filter((f) => f.required),
    [],
  );
  const optionalFields = useMemo(
    () => LEGACY_CUSTOMER_FIELDS.filter((f) => !f.required),
    [],
  );

  const handleFormChange = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setResult(null);
  };

  const handleSingleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setResult(null);
    try {
      const response = await LegacyCustomerImportService.importOne(form);
      setResult(response);
      if (response.success) {
        setForm(EMPTY_FORM);
      }
    } catch (err) {
      setResult({ success: false, error: err.message || 'Import failed' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.csv')) {
      alert('Please select a CSV file');
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const rows = parseLegacyCustomerCsv(event.target.result);
        setSelectedFile(file);
        setPreviewRows(rows.slice(0, 5));
        setCsvErrors([]);
        setResult(null);
      } catch (error) {
        alert(error.message || 'Failed to parse CSV');
        setSelectedFile(null);
        setPreviewRows([]);
      }
    };
    reader.readAsText(file);
  };

  const handleCsvUpload = async () => {
    if (!selectedFile) {
      alert('Please select a CSV file first');
      return;
    }

    setCsvUploading(true);
    setCsvErrors([]);
    setCsvProgress({ total: 0, processed: 0, imported: 0, failed: 0 });

    try {
      const text = await selectedFile.text();
      const rows = parseLegacyCustomerCsv(text);
      const summary = await LegacyCustomerImportService.importBatch(rows, setCsvProgress);
      setCsvErrors(summary.errors || []);
      setResult({
        success: summary.success,
        batch: true,
        imported: summary.imported,
        failed: summary.failed,
        total: summary.total,
        message: summary.message,
      });
    } catch (err) {
      setResult({ success: false, error: err.message || 'CSV upload failed' });
    } finally {
      setCsvUploading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-primary-100 text-primary-700 flex items-center justify-center">
            <UserPlus size={22} />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Add Customer</h1>
            <p className="text-gray-600 mt-1">
              Import legacy membership customers — single form or bulk CSV upload
            </p>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-2 flex gap-2 w-fit">
        <button
          type="button"
          onClick={() => setActiveTab('single')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
            activeTab === 'single' ? 'bg-primary-600 text-white' : 'text-gray-700 hover:bg-gray-50'
          }`}
        >
          Single Customer
        </button>
        <button
          type="button"
          onClick={() => setActiveTab('bulk')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
            activeTab === 'bulk' ? 'bg-primary-600 text-white' : 'text-gray-700 hover:bg-gray-50'
          }`}
        >
          Bulk CSV Upload
        </button>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        <div className="xl:col-span-2 space-y-6">
          {activeTab === 'single' ? (
            <form onSubmit={handleSingleSubmit} className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 space-y-6">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Customer Details</h2>
                <p className="text-sm text-gray-600 mt-1">Fill mandatory fields to create account + active membership</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {LEGACY_CUSTOMER_FIELDS.map((field) => (
                  <label key={field.key} className="block">
                    <span className="text-sm font-medium text-gray-700">
                      {field.label}
                      {field.required ? <span className="text-red-500 ml-1">*</span> : null}
                    </span>
                    <input
                      type="text"
                      value={form[field.key]}
                      onChange={(e) => handleFormChange(field.key, e.target.value)}
                      placeholder={field.example}
                      className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                    <span className="text-xs text-gray-500 mt-1 block">{field.hint}</span>
                  </label>
                ))}
              </div>

              <button
                type="submit"
                disabled={submitting}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-60"
              >
                {submitting ? <Loader2 size={18} className="animate-spin" /> : <UserPlus size={18} />}
                <span>{submitting ? 'Importing...' : 'Add Customer'}</span>
              </button>
            </form>
          ) : (
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 space-y-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">Bulk CSV Import</h2>
                  <p className="text-sm text-gray-600 mt-1">Upload previous customer data in one go</p>
                </div>
                <button
                  type="button"
                  onClick={downloadLegacyCustomerSampleCsv}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700"
                >
                  <Download size={18} />
                  <span>Download Sample CSV</span>
                </button>
              </div>

              <div className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center">
                <input
                  type="file"
                  accept=".csv"
                  onChange={handleFileSelect}
                  disabled={csvUploading}
                  className="hidden"
                  id="legacy-customer-csv"
                />
                <label
                  htmlFor="legacy-customer-csv"
                  className={`cursor-pointer flex flex-col items-center ${csvUploading ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <Upload size={44} className="text-gray-400 mb-3" />
                  <p className="text-gray-700 font-medium">
                    {selectedFile ? selectedFile.name : 'Click to select CSV file'}
                  </p>
                  <p className="text-sm text-gray-500 mt-1">Columns with * in sample are mandatory</p>
                </label>
              </div>

              {previewRows.length > 0 ? (
                <div className="overflow-x-auto border border-gray-200 rounded-lg">
                  <table className="min-w-full text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 py-2 text-left font-semibold text-gray-700">Name</th>
                        <th className="px-3 py-2 text-left font-semibold text-gray-700">Phone</th>
                        <th className="px-3 py-2 text-left font-semibold text-gray-700">Car No.</th>
                        <th className="px-3 py-2 text-left font-semibold text-gray-700">Model</th>
                        <th className="px-3 py-2 text-left font-semibold text-gray-700">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {previewRows.map((row, idx) => (
                        <tr key={idx} className="border-t border-gray-100">
                          <td className="px-3 py-2">{row.name}</td>
                          <td className="px-3 py-2">{row.contact_number}</td>
                          <td className="px-3 py-2">{row.car_number}</td>
                          <td className="px-3 py-2">{row.car_model}</td>
                          <td className="px-3 py-2">{row.paid_amount}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : null}

              {csvUploading ? (
                <div className="rounded-lg bg-gray-50 p-4 text-sm text-gray-700">
                  Processing {csvProgress.processed} / {csvProgress.total} rows...
                  {' '}(Imported: {csvProgress.imported}, Failed: {csvProgress.failed})
                </div>
              ) : null}

              <button
                type="button"
                onClick={handleCsvUpload}
                disabled={!selectedFile || csvUploading}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-60"
              >
                {csvUploading ? <Loader2 size={18} className="animate-spin" /> : <Upload size={18} />}
                <span>{csvUploading ? 'Uploading...' : 'Upload CSV'}</span>
              </button>
            </div>
          )}

          {result ? (
            <div
              className={`rounded-xl border p-4 ${
                result.success
                  ? 'border-green-200 bg-green-50 text-green-900'
                  : 'border-red-200 bg-red-50 text-red-900'
              }`}
            >
              <div className="flex items-start gap-3">
                {result.success ? <CheckCircle size={20} className="mt-0.5" /> : <AlertCircle size={20} className="mt-0.5" />}
                <div>
                  {result.batch ? (
                    <>
                      <p className="font-semibold">
                        {result.success ? 'Bulk import successful' : 'Bulk import cancelled'}
                      </p>
                      <p className="text-sm mt-1">
                        {result.success
                          ? `All ${result.imported} customer(s) imported successfully.`
                          : result.message || `No rows imported. Total: ${result.total} · Errors: ${result.failed}`}
                      </p>
                    </>
                  ) : result.success ? (
                    <>
                      <p className="font-semibold">Customer imported successfully</p>
                      <p className="text-sm mt-1">
                        Plan: {result.data?.plan_name || result.data?.plan_slug} · Status: {result.data?.membership_status}
                      </p>
                    </>
                  ) : (
                    <>
                      <p className="font-semibold">Import failed</p>
                      <p className="text-sm mt-1">{result.error}</p>
                    </>
                  )}
                </div>
              </div>
            </div>
          ) : null}

          {csvErrors.length > 0 ? (
            <div className="bg-white rounded-xl border border-red-200 shadow-sm p-4">
              <h3 className="font-semibold text-red-800 mb-3">Import errors ({csvErrors.length})</h3>
              <div className="max-h-64 overflow-y-auto space-y-2">
                {csvErrors.slice(0, 50).map((err, idx) => (
                  <div key={idx} className="text-sm border border-red-100 rounded-lg p-2 bg-red-50">
                    <span className="font-medium">Row {err.row}</span>
                    {err.name ? ` · ${err.name}` : ''}
                    {err.contact_number ? ` · ${err.contact_number}` : ''}
                    <div className="text-red-700 mt-1">{err.error}</div>
                  </div>
                ))}
              </div>
            </div>
          ) : null}
        </div>

        <div className="space-y-6">
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
            <div className="flex items-center gap-2 mb-4">
              <FileText size={18} className="text-primary-600" />
              <h2 className="text-lg font-semibold text-gray-900">Field Guide</h2>
            </div>

            <div className="space-y-5">
              <div>
                <h3 className="text-sm font-semibold text-red-700 mb-2">Mandatory (*)</h3>
                <ul className="space-y-2">
                  {requiredFields.map((field) => (
                    <li key={field.key} className="text-sm">
                      <span className="font-medium text-gray-900">{field.label}</span>
                      <span className="text-gray-500 block text-xs mt-0.5">{field.hint}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-2">Optional</h3>
                <ul className="space-y-2">
                  {optionalFields.map((field) => (
                    <li key={field.key} className="text-sm">
                      <span className="font-medium text-gray-900">{field.label}</span>
                      <span className="text-gray-500 block text-xs mt-0.5">{field.hint}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-sm text-blue-900">
            <p className="font-semibold mb-2">Data kahan save hota hai</p>
            <ul className="list-disc pl-5 space-y-1 mb-3">
              <li><strong>users</strong> — login (phone OTP)</li>
              <li><strong>customers</strong> — profile, address, pincode</li>
              <li><strong>customer_vehicles</strong> — gaadi number, model</li>
              <li><strong>customer_membership_subscriptions</strong> — plan, card, active/expiry</li>
            </ul>
            <p className="font-semibold mb-2">Auto-calculated on import</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Plan label in app: <strong>Physical card</strong> (not Old Customer)</li>
              <li>Customer uploads card photo in app → admin verifies → RSA active</li>
              <li>Expiry from expiry_datetime, or delivered date + 15 years</li>
              <li>Vehicle brand from car model</li>
              <li>Email from phone if blank</li>
              <li>Services: <code>services_used</code> + <code>services_remaining</code> (optional)</li>
            </ul>
          </div>

          <div className="bg-green-50 border border-green-200 rounded-xl p-4 text-sm text-green-900">
            <p className="font-semibold mb-2">App me customer flow</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Legacy gaadi par active plan hai → app ke naye plans us gaadi ke liye nahi dikhenge</li>
              <li>Plan expire hone ke baad → customer app se naya plan kharid sakta hai</li>
              <li>Dusri gaadi add kare → wahan normal plan purchase kaam karega</li>
            </ul>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-900">
            <p className="font-semibold mb-2">Bulk CSV rule</p>
            <p>
              Large files upload in batches of 75 rows. Progress dikhega: &quot;Processing X / Y rows&quot;.
              Agar beech me error aaye to us batch se pehle wali rows save ho chuki hongi.
            </p>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-900">
            <p className="font-semibold mb-2">Before bulk upload</p>
            <p>
              Run <code className="bg-white/70 px-1 rounded">supabase/admin-legacy-customer-import.sql</code> once
              in Supabase SQL Editor to enable import RPCs.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddCustomer;
